import { z } from "zod";
import { zPlainText, zPlainTextRequired, zSafeHtml } from "../middleware/zod-sanitize.js";
import { formCategories } from "./../../helpers/index.js";
import { templateEditCases } from "./forms.constant.js";
import {
  objectIdString,
  filterWhereClause,
  filterWhereClauseFor,
  sortConfigFor,
  dynamicCustomFieldPatterns,
  sortConfig,
  extendStatusResponse,
} from "../../helpers/schemas/common.schema.js";

// Per-endpoint filterable-column allow-lists. Each grid below builds a field-restricted
// `where` from these (plus dynamic custom-field / form-question column patterns) instead of
// the OPEN shared `filterWhereClause`, so a client can only filter on columns the grid
// actually renders. `gridValueSchema` (shared) keeps the open clause for endpoints not yet
// restricted; restricted endpoints override `where` via a per-endpoint value schema.
const templateTypesListFilterableFields = ["name", "description", "createdDate", "createdBy"] as const;
const templateTypesListWhere = filterWhereClauseFor(templateTypesListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const templatesListFilterableFields = [
  "name",
  "description",
  "type.name",
  "createdDate",
  "createdBy.fullName",
  "_id",
  "isArchived",
  // Programmatic base filters (form-template pickers scope by project / type)
  "projectId",
  "type._id",
] as const;
const templatesListWhere = filterWhereClauseFor(templatesListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const tipfListFilterableFields = [
  "counter",
  "title",
  "description",
  "space.spaceName",
  "status",
  "selectedForm.name",
  "selectedForm.type.name",
  "selectedFormFlow.name",
  "createdBy.fullName",
  "currentLevelName",
  "createdDate",
  "dueDate",
  "lastAction.userName",
  "lastAction.actionName",
  "lastAction.timeStamp",
  "currentLevelOfForm",
  "isArchived.status",
  "isApproved",
  "_id",
] as const;
const tipfListWhere = filterWhereClauseFor(tipfListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});
// Sortable columns for the form-instances grid DIVERGE from its filterable set: the grid renders
// four extra sortable-only columns (all columns are sortable in view mode — see SlickGrid1 wrapper)
// that carry no `filterType`, so they are not in tipfListFilterableFields:
//   - formProgress            (client-computed progress column)
//   - contractName / contractType / contractOrgName ($addFields-derived AFTER the $sort stage)
// They must still be accepted by the sort allow-list or clicking those headers would 400. Sorting on
// them is a server-side no-op (field absent at the $sort stage) — no worse than the non-indexed
// columns already allowed. Superset = every filterable column (all sortable) UNION the four extras.
export const tipfListSortableFields = [
  ...tipfListFilterableFields,
  "formProgress",
  "contractName",
  "contractType",
  "contractOrgName",
] as const;
const tipfListSorted = sortConfigFor(tipfListSortableFields, { patterns: dynamicCustomFieldPatterns });

const libraryEntitiesFilterableFields = [
  "data.resourceType",
  "data.formCategory",
  "name",
  "selectedForm",
  "space.spaceName",
  "createdBy",
  "createdDate",
  "_id",
] as const;
const libraryEntitiesWhere = filterWhereClauseFor(libraryEntitiesFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

const formFlowListFilterableFields = [
  "name",
  "selectedForm",
  "space.spaceName",
  "createdBy",
  "createdDate",
  "_id",
] as const;
const formFlowListWhere = filterWhereClauseFor(formFlowListFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

// Base response schema with result and errorMessage
const formStatusResponse = extendStatusResponse({
  result: z.array(z.any()).optional(),
  count: z.number().nonnegative().optional(),
});

const gridValueSchema = z.object({
  skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
  take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max rows to return)." }),
  sorted: z.array(sortConfig).optional().meta({ description: "Grid sort configuration (column + direction)." }),
  where: filterWhereClause.meta({
    description: "Grid filter clause; overridden by a field-restricted allow-list in most list endpoints.",
  }),
  requiresCounts: z
    .boolean()
    .optional()
    .meta({ description: "If true, also returns the total matching-row count for pagination." }),
});

// Field types enum for question fields
export const fieldTypeEnum = z.enum([
  "single-line",
  "multi-line",
  "date-time",
  "image",
  "fileUpload",
  "single-select",
  "multi-select",
  "users",
  "look-up",
  "number",
  "url",
  "geoLocation",
  "text",
  "tableDataSource",
  "actualStartDate",
  "actualEndDate",
  "progress",
  "remDuration",
  "formula",
  "concatenate",
  "autoNumber",
]);

const answerSchema = z.object({
  value: z.unknown().optional().meta({
    description:
      "The stored answer for a question field. Shape depends on the field's fieldType (string for text/url, epoch-ms number for date-time, array for multi-select/users, awsKey/base64 metadata for image/fileUpload, number for number/formula/autoNumber).",
  }),
  createdDate: z
    .number()
    .optional()
    .meta({ description: "Epoch milliseconds when this answer was written; set server-side, not by the caller." }),
  createdBy: objectIdString
    .optional()
    .meta({ description: "Account _id (ObjectId 24-hex string) of the user who wrote this answer; set server-side." }),
});

const attachmentsDataSchema = z.object({
  awsKey: z
    .string()
    .optional()
    .meta({ description: "S3 object key of the uploaded attachment (as returned by uploadFilesInTemplate)." }),
  name: z.string().optional().meta({ description: "Display file name of the attachment." }),
  type: z.string().optional().meta({ description: "MIME type of the attachment, e.g. image/png or application/pdf." }),
});

/**
 * A formula field's expression must be plain arithmetic over the mapped numeric
 * field variables. We bound its length and restrict it to letters/digits (incl.
 * unicode variable names), whitespace and the arithmetic operators. This rejects
 * the structural characters used by ranges (`[1:1e9]`), matrices, block/assignment
 * expressions, and the factorial operator (`!`) — all of which can make client-side
 * evaluation unbounded in time/memory. This is the server-side defence-in-depth
 * layer; the frontend additionally restricts the parsed AST node types.
 */
const MAX_FORMULA_EXPRESSION_LENGTH = 1000;
const SAFE_FORMULA_EXPRESSION_REGEX = /^[\p{L}\p{N}_\s+\-*/%^().,]*$/u;
const safeFormulaExpression = z
  .string()
  .max(MAX_FORMULA_EXPRESSION_LENGTH)
  .refine((value) => SAFE_FORMULA_EXPRESSION_REGEX.test(value), {
    message: "Formula contains unsupported characters",
  });

export const questionFieldSchema = z
  .object({
    _id: z.string().length(36).meta({
      description:
        "Stable 36-char UUID (not a Mongo ObjectId) identifying this question within its section; used as the arrayFilter key when cascading answers/edits into live form instances. Reuse the same UUID to edit an existing question; generate a fresh one to add.",
    }),
    fieldType: fieldTypeEnum.meta({
      description:
        "The question's input/behaviour type, which drives how the answer is captured and validated. Special non-input types: formula (computed from other numeric fields), autoNumber (auto-incrementing counter), concatenate (joins other fields), look-up (Location/Teams), tableDataSource (grid), progress/remDuration/actualStartDate/actualEndDate (task-form fields).",
    }),
    fieldLabel: z.string().optional().meta({
      description:
        "Optional short CODE / identifier for the question (e.g. 'MBOOK_NO', 'SES_NO' — used by SAP and formula/variable mappings). This is NOT the text shown to users and is rarely set — leave it unset unless you specifically need a code key. The displayed question prompt is `questionValue`, not this.",
    }),
    questionValue: z.string().optional().meta({
      description:
        "The human-readable question label/prompt shown to the user — THIS is the primary question text (e.g. 'Name of Work', 'Date of Test', 'Average'). Set it on EVERY question: a question with no questionValue renders with a blank/undefined label. (Despite the name, this is the label, not an answer value.)",
    }),
    fieldHeading: z.string().optional().meta({
      description:
        "The answer-type display name shown as the question's type chip (e.g. 'Single Line', 'Number', 'Date & Time', 'Look Up') — the human name of the chosen fieldType. Set it alongside questionValue to match the standard form-builder output.",
    }),
    isMandatory: z.boolean().optional().meta({
      description:
        "If true, the answer is required; enforced server-side by checkAllMandatoryFields before a form section can be submitted.",
    }),
    answer: answerSchema.optional().meta({
      description:
        "Prefilled/default answer on a template is unusual; on a live form instance this holds the captured answer. Normally written via the updateTaskInspectionAndProgress answer path, not sent at template-build time.",
    }),
    allowPreDefinedColValues: z.boolean().optional().meta({
      description: "Table field config: whether predefined column values are allowed for a tableDataSource question.",
    }),
    answerTrack: z.record(z.string(), answerSchema).optional().meta({
      description:
        "Per-row/per-key answer history keyed by row or occurrence id; used for table/multi-instance questions. Managed server-side.",
    }),
    minFieldLimit: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "Minimum allowed value/length for number or text fields; null = no minimum." }),
    maxFieldLimit: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "Maximum allowed value/length for number or text fields; null = no maximum." }),
    placeholderText: z.string().optional().meta({ description: "Placeholder text shown in the empty input." }),
    selectionScope: z
      .enum([
        "futureDateTime",
        "pastDateTime",
        "rangeBased",
        "anyDateTime",
        "currentDateTime",
        "currentSection",
        "entireForm",
        "space",
        "project",
      ])
      .optional()
      .meta({
        description:
          "Constrains selectable values. For date-time fields: futureDateTime/pastDateTime/anyDateTime/currentDateTime/rangeBased. For look-up/reference fields: currentSection/entireForm/space/project scope of the lookup source.",
      }),
    minFieldRange: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For rangeBased date-time selectionScope: lower bound as epoch ms; null = unbounded." }),
    maxFieldRange: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For rangeBased date-time selectionScope: upper bound as epoch ms; null = unbounded." }),
    minNoOfFiles: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For image/fileUpload fields: minimum number of files required; null = no minimum." }),
    maxNoOfFiles: z
      .number()
      .optional()
      .nullable()
      .meta({ description: "For image/fileUpload fields: maximum number of files allowed; null = no maximum." }),
    answerChoices: z
      .array(z.string())
      .optional()
      .meta({ description: "The option list for single-select/multi-select fields." }),
    lookUpType: z.enum(["Location", "Teams"]).optional().meta({
      description:
        "For look-up fields: which entity set the lookup draws from — project Locations or Teams. Aggregated per template into Forms.lookUpUsed.",
    }),
    allowedFileTypes: z
      .array(z.enum(["image/jpg", "image/jpeg", "application/pdf", "image/png"]))
      .optional()
      .meta({
        description:
          "For image/fileUpload fields: permitted MIME types; empty array means no restriction. Must match the server's multer allow-list (jpg/jpeg/png/pdf).",
      }),
    uploadSource: z.enum(["FILE_MANAGER", "LOCAL"]).optional().meta({
      description: "For fileUpload fields: whether files come from the CUBE File Manager or a local device upload.",
    }),
    attachmentsData: z
      .array(attachmentsDataSchema)
      .optional()
      .meta({ description: "Predefined attachments bundled with the question (awsKey/name/type each)." }),
    captureLiveLocation: z.boolean().optional().meta({
      description: "For geoLocation fields: if true, capture the device's live GPS location rather than manual entry.",
    }),
    allowManualSelection: z.boolean().optional().meta({
      description:
        "For geoLocation/look-up fields: whether the user may manually pick a value instead of an auto-detected one.",
    }),
    viewType: z
      .enum(["list", "dropDown", "chip", "card"])
      .optional()
      .meta({ description: "Render style for select/look-up fields." }),
    dataToDisplay: z
      .array(z.any())
      .optional()
      .meta({ description: "For look-up fields: which attributes of the looked-up entity to display in the answer." }),
    lookUpAnsFieldType: z
      .string()
      .optional()
      .meta({ description: "For look-up fields: the effective field type of the resolved lookup answer." }),
    allowEditingOfPredefinedVal: z
      .boolean()
      .optional()
      .meta({ description: "Whether a user may edit a predefined/preset answer value." }),
    allowPredefinedVal: z
      .boolean()
      .optional()
      .meta({ description: "Whether this field carries a predefined default value." }),
    displayAs: z.enum(["button", "text", "dateAndTime", "dateOnly"]).optional().meta({
      description: "Presentation variant: for date-time (dateAndTime/dateOnly) or url/action fields (button/text).",
    }),
    openIn: z
      .enum(["newTab", "popUp", "sameTab"])
      .optional()
      .meta({ description: "For url fields: where the link opens when clicked." }),
    formulaExpression: safeFormulaExpression.optional().meta({
      description:
        "For formula fields: the arithmetic expression evaluated over mapped field variables. Max 1000 chars; restricted to letters/digits/underscore/whitespace and + - * / % ^ ( ) . , — ranges, matrices, and factorial are rejected server-side.",
    }),
    fieldsVariableMapper: z.record(z.string().length(36), z.string()).optional().meta({
      description:
        "For formula fields: maps each source question's 36-char UUID (key) to the variable name used in formulaExpression (value).",
    }),
    displaySize: z.number().optional().meta({ description: "Layout width/size hint for rendering the field." }),
    seperator: z.string().optional().meta({
      description:
        "For concatenate/autoNumber fields: the string inserted between joined field values (note spelling).",
    }),
    selectedFields: z.array(z.string().length(36)).optional().meta({
      description: "For concatenate fields: ordered list of source question 36-char UUIDs whose values are joined.",
    }),
    numberingMode: z.enum(["standalone", "basedOnFields"]).optional().meta({
      description:
        "For autoNumber fields: standalone assigns a portal/form sequential counter (auto-populated at form creation); basedOnFields derives the number from other fields and is NOT auto-assigned on create.",
    }),
  })
  .catchall(z.unknown());

export const formTemplateFieldSchema = z.object({
  _id: z.string().optional().meta({
    description:
      "36-char UUID identifying this section within the template (not a Mongo ObjectId); used as the arrayFilter key when cascading edits into live forms. Reuse to edit an existing section, generate a new one to add.",
  }),
  sectionCode: z.string().optional().meta({
    description:
      "Preset-section marker. Absent/empty = a normal user-defined section. Set to a presetSectionsCodeObject value (e.g. SECTION_TYPE_QUANTITY, SECTION_TYPE_MILESTONE, SECTION_TYPE_WBS_ITEMS, SECTION_TYPE_TASK) to make this a special contract section with built-in behaviour.",
  }),
  isDefaultSection: z.boolean().optional().meta({ description: "Marks this as the template's default/first section." }),
  sectionHeading: z.string().meta({ description: "Display title of the section." }),
  questionFields: z
    .array(questionFieldSchema)
    .optional()
    .meta({ description: "Ordered list of question fields in this section." }),
  tableDataSource: z
    .array(z.any())
    .optional()
    .meta({ description: "For table-type sections: the predefined rows/grid data for the section." }),
  allowNewRowAddInForm: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether users may add new rows while filling the form." }),
  allowPreDefineRowValues: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether predefined row values are provided." }),
  allowEditPreDefineRowInForm: z
    .boolean()
    .optional()
    .meta({ description: "For table sections: whether users may edit predefined rows while filling the form." }),
});

export type QuestionField = z.infer<typeof questionFieldSchema>;
export type FormTemplateField = z.infer<typeof formTemplateFieldSchema>;

const templateTypeSchema = z.object({
  _id: objectIdString.meta({
    description:
      "Forms_type _id (ObjectId 24-hex string) selecting which form-type category this template belongs to; obtain from the formTypesController listing, NOT the type's display name.",
  }),
  name: z
    .string()
    .optional()
    .meta({ description: "Display name of the form type (denormalized copy of the Forms_type name)." }),
  resourceType: z
    .string()
    .optional()
    .meta({ description: "Form category string this type belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
});

const formFlowUserSchema = z.object({
  id: objectIdString.meta({
    description:
      "Account _id (ObjectId 24-hex string) of a user permitted for this flow role; obtain from a space/project users list, not the display name.",
  }),
  name: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "Display name of the user (denormalized for UI; not authoritative)." }),
});

const formFlowSectionSchema = z.object({
  _id: z.string().length(36).meta({
    description: "36-char UUID of a template section this flow level acts on (matches formTemplateFieldSchema._id).",
  }),
  sectionHeading: z.string().meta({ description: "Display heading of the referenced section." }),
  sectionCode: z.string().nullable().optional().meta({
    description: "Preset-section code of the referenced section, if it is a preset section; null/absent otherwise.",
  }),
});

const gridDataSourceSchema = z.object({
  type: z.enum(["Fill", "Modify", "Verify"]).meta({
    description:
      "The action this data-source grants at this level: Fill (answer), Modify (edit), or Verify (approve section).",
  }),
  sections: z
    .array(formFlowSectionSchema)
    .optional()
    .meta({ description: "Template sections this Fill/Modify/Verify permission applies to at this level." }),
  users: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users granted the given action on the listed sections at this level." }),
});

const formFlowActionDetailsSchema = z.object({
  userId: objectIdString.optional().meta({
    description:
      "Account id of the user who took the last action at this level; set server-side on submit/approve/reject.",
  }),
  userName: z.string().optional().meta({ description: "Display name of the acting user; set server-side." }),
  actionName: z
    .string()
    .optional()
    .meta({ description: "The action taken (e.g. SUBMITTED/APPROVED/REJECTED); set server-side." }),
  actionMessage: z
    .string()
    .optional()
    .meta({ description: "Message/comment attached to the action; set server-side." }),
  message: z
    .string()
    .optional()
    .meta({ description: "Alternate/legacy comment field for the action; set server-side." }),
  timeStamp: z.number().optional().meta({ description: "Epoch ms when the action occurred; set server-side." }),
  signDocAwsKey: z
    .string()
    .optional()
    .nullable()
    .meta({ description: "S3 key of the signature document attached to the action, if any; set server-side." }),
});

const formFlowLevelSchema = z.object({
  dataSource: z.array(gridDataSourceSchema).meta({
    description:
      "The Fill/Modify/Verify permission grids (section+user) that define who can act on which sections at this approval level.",
  }),
  heading: z.string().min(1).max(100).meta({ description: "Display name of this approval level/step." }),
  submitBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Submit button at this level." }),
  allowDynamicUserConfig: z.boolean().optional().meta({
    description:
      "If true, the allowed users at this level may be reassigned at fill-time rather than fixed by the template.",
  }),
  rejectBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Reject button at this level." }),
  approveBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Approve button at this level." }),
  rollBackBtnTxt: z
    .string()
    .max(50)
    .optional()
    .meta({ description: "Custom label for the Roll-back button at this level." }),
  allowedUsersToSubmit: z.array(formFlowUserSchema).optional().meta({
    description: "Users permitted to submit this level; replaced by usersData when instantiating from library.",
  }),
  allowedUsersToReject: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to reject at this level." }),
  allowedUsersToRollBack: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to roll back at this level." }),
  allowedUsersToApprove: z
    .array(formFlowUserSchema)
    .optional()
    .meta({ description: "Users permitted to approve at this level." }),
  actionStatus: z.boolean().optional().meta({
    description:
      "Runtime state of this level's action on a live form instance; managed server-side, not set at template-build time.",
  }),
  actionDetails: formFlowActionDetailsSchema
    .optional()
    .meta({ description: "Runtime record of the action taken at this level on a live form; populated server-side." }),
});

// Form Flow building-block types (derived from the zod schemas above).
export type FormFlowUser = z.infer<typeof formFlowUserSchema>;
export type FormFlowSection = z.infer<typeof formFlowSectionSchema>;
export type formFlowDataSource = z.infer<typeof gridDataSourceSchema>;
export type FormFlowActionDetails = z.infer<typeof formFlowActionDetailsSchema>;
export type FormFlowStep = z.infer<typeof formFlowLevelSchema>;

const selectedFormSchema = z.object({
  _id: objectIdString.meta({
    description:
      "Forms template _id (ObjectId 24-hex string) this flow is built on; get from the formController listing. Marked formIsInUse=true when the flow is saved.",
  }),
  name: z.string().optional().meta({ description: "Denormalized template name." }),
  description: z.string().optional().meta({ description: "Denormalized template description." }),
  type: z
    .object({
      _id: objectIdString.meta({ description: "Forms_type _id of the template's type category." }),
      name: z.string().optional().meta({ description: "Denormalized form-type name." }),
      description: z.string().optional().meta({ description: "Denormalized form-type description." }),
    })
    .optional()
    .meta({ description: "The template's form-type category (denormalized)." }),
  template: z
    .array(formTemplateFieldSchema)
    .meta({ description: "The full section/question structure copied from the Forms template." }),
});

const linkedFormSchema = z.object({
  _id: objectIdString.meta({
    description: "InspectionAndProgress form-instance _id (ObjectId 24-hex string) being linked into this form.",
  }),
  resourceType: z
    .string()
    .meta({ description: "Form category of the linked form instance (e.g. INSPECTION, PROGRESS_UPDATES)." }),
});

export const addSectionToLibraryRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "Source Forms template _id (ObjectId 24-hex string) the section is copied from; obtain from the formController listing. Stored as data.formId on the Library entry.",
    }),
    sectionId: z.string().length(36).meta({
      description:
        "36-char UUID of the section (matches formTemplateFieldSchema._id). This is the UPSERT KEY: an existing Library section with the same sectionId in this portal is overwritten. Not a Mongo ObjectId.",
    }),
    sectionHeading: zPlainTextRequired.meta({ description: "Display heading stored for the library section." }),
    resourceType: z.string().meta({
      description:
        "Form category the section belongs to (e.g. INSPECTION). Stored on the Library entry but NOT used as part of the upsert match key (only sectionId + portalId are matched).",
    }),
    questionFields: z
      .array(questionFieldSchema)
      .optional()
      .meta({ description: "The question fields captured into the reusable library section." }),
    sectionCode: z
      .string()
      .optional()
      .meta({ description: "Preset-section code, if the section is a preset section." }),
    allowNewRowAddInForm: z
      .boolean()
      .optional()
      .meta({ description: "Table-section flag: allow adding new rows at fill time." }),
    allowPreDefineRowValues: z
      .boolean()
      .optional()
      .meta({ description: "Table-section flag: predefined row values are provided." }),
    tableDataSource: z.array(z.any()).optional().meta({ description: "Table-section predefined rows/grid data." }),
  })
  .meta({
    description:
      "Saves (upserts) a reusable form-template Section into the portal's Library collection as a TEMPLATE_SECTION entry, matched by sectionId within the caller's portalId so one tenant cannot overwrite another's. Pass the source formId (a Forms template _id) plus the section's fields (sectionId is a 36-char UUID, sectionHeading, questionFields, etc.); portalId and the author are taken from the session, not the body. No dedicated permission check runs in the service.",
  });

export const addSectionToLibraryResponse = formStatusResponse.meta({
  description: "Response for add section to library",
});

export const uploadFilesInTemplateResponse = extendStatusResponse({
  // S3 key (uuid) of the first uploaded file — convenience for single-file uploads
  uuid: z.string().optional(),
  uploadedFiles: z
    .array(
      z.object({
        uuid: z.string(),
        fileName: z.string(),
        mimeType: z.string(),
        size: z.number().nonnegative(),
      }),
    )
    .optional(),
}).meta({
  description: "Response for uploading files in a form template (returns S3 uuid keys)",
});

export const getContractFormBoqItemsRequest = z
  .object({
    contractId: objectIdString.meta({
      description: "Contract _id (ObjectId 24-hex string); obtain from the contracts listing, not the contract name.",
    }),
    contractVersionId: objectIdString.meta({
      description: "The specific Contract version _id (ObjectId 24-hex string) to read forms/BOQ items from.",
    }),
    resourceType: z.enum([formCategories.inspection, formCategories.progressUpdates, formCategories.bills]).meta({
      description:
        "Form category to aggregate BOQ items for; must be one of the inspection, progressUpdates, or bills category constants.",
    }),
  })
  .meta({
    description:
      "Read-only: for a Contract version, lists all non-deleted forms of the given category the caller can view and, for each, returns formId/formName/formNo/status/archived flag, the form template, and its attached BOQ items (fetched per-form via getFormBoqItems). Requires RESOURCE_VIEW permission on the contract. Inputs: contractId and contractVersionId (a Contract and one of its version ids), plus resourceType which must be one of the inspection, progressUpdates, or bills form categories. GET route; query params are converted to the body. Differs from getFormBoqItems (single form) by aggregating BOQ items across all forms on a contract version.",
  });

export const getContractFormBoqItemsResponse = formStatusResponse.meta({
  description: "Response for get contract form boq items",
});

const fetchFormEntriesFilterableFields = [
  "counter",
  "title",
  "description",
  "lastAction.actionName",
  "isArchived.status",
  "status",
  "lastAction.timeStamp",
  "lastAction.userName",
] as const;
const fetchFormEntriesWhere = filterWhereClauseFor(fetchFormEntriesFilterableFields, {
  patterns: dynamicCustomFieldPatterns,
});

export const fetchFormEntriesRequest = z
  .object({
    value: z.object({
      formId: objectIdString.meta({
        description:
          "Forms TEMPLATE _id (ObjectId 24-hex string) — the selectedForm._id whose instances to list; get from the formController listing. NOT a form-instance id.",
      }),
      projectId: objectIdString.meta({
        description:
          "Project _id (ObjectId 24-hex string) scoping the returned form instances; from the projects listing.",
      }),
      getExtractedEntries: z.boolean().optional().meta({
        description:
          "If true, returns the flattened/extracted answer-entries grid instead of the standard instance grid rows.",
      }),
      requiresCounts: z
        .boolean()
        .optional()
        .meta({ description: "If true, also returns the total matching-instance count for pagination." }),
      skip: z.number().int().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
      take: z
        .number()
        .int()
        .nonnegative()
        .max(1000000)
        .optional()
        .meta({ description: "Pagination page size (max rows to return)." }),
      where: fetchFormEntriesWhere.meta({
        description:
          "Grid filter clause restricted to the allowed columns (counter, title, description, status, lastAction.*, isArchived.status) plus dynamic custom-field/question columns.",
      }),
      // Sortable columns == the filterable allow-list (all base grid columns carry a filterType and
      // are sortable; formFlow.heading is disableSorting). formsPatch=true lets the query builder sort
      // on a form-question column keyed by its UUID — allowed via the form-question pattern in
      // dynamicCustomFieldPatterns.
      sorted: sortConfigFor(fetchFormEntriesFilterableFields, { patterns: dynamicCustomFieldPatterns }),
    }),
  })
  .meta({
    description:
      "Lists the form instances (InspectionAndProgress documents) created from a given Form template within a Project, with grid where/sorted/skip/take/requiresCounts and an optional getExtractedEntries mode; returns per-instance grid data (title, status, currentLevelOfForm, answers). value.formId is the Forms template _id (selectedForm._id, e.g. from a Forms list) and value.projectId is the Project _id. Only returns instances where the session user is in users[] with RESOURCE_VIEW, and only latest-version instances for contract forms.",
  });

export const fetchFormEntriesResponse = formStatusResponse.meta({
  description: "Response for fetch form entries",
});

// Service uses: entity, sorted, where, skip, take, requiresCounts
// Controller spreads: req.body.value
export const getEntitesFromLibraryRequest = z
  .object({
    value: z
      .object({
        entity: z.enum(["TEMPLATE_SECTION", "FORM_FLOW"]).optional().meta({
          description:
            "The Library entry type to list — matched exactly against Library.entity. Use TEMPLATE_SECTION for saved sections or FORM_FLOW for saved flows. This is the ONLY content filter applied (besides portal scope).",
        }),
        resourceType: z.string().optional().meta({
          description:
            "IGNORED by the service: getEntitesFromLibrary matches only on entity + portalId and never reads resourceType. Passing it has no effect.",
        }),
        skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (entries to skip)." }),
        take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max entries)." }),
        sorted: sortConfigFor(libraryEntitiesFilterableFields, { patterns: dynamicCustomFieldPatterns }).meta({
          description:
            "Grid sort configuration; defaults to createdDate descending. Restricted to the library grid's sortable columns (name, selectedForm, space.spaceName, createdBy, createdDate).",
        }),
        where: libraryEntitiesWhere.meta({
          description:
            "Optional grid filter clause over allowed library columns (name, createdBy, createdDate, _id, space.spaceName, data.resourceType, data.formCategory) plus dynamic custom-field patterns.",
        }),
        requiresCounts: z
          .boolean()
          .optional()
          .meta({ description: "If true, also returns the total matching-entry count." }),
      })
      .optional(),
  })
  .meta({
    description:
      "Lists reusable Library entries for the current portal, filtered by the value.entity type (e.g. TEMPLATE_SECTION or FORM_FLOW) with optional grid where/sorted/skip/take and requiresCounts; returns each entry's flattened data with _id, createdBy and createdDate. Read-only, scoped to the session portalId. Use this to browse saved sections/form-flows before reusing one.",
  });

export const getEntitesFromLibraryResponse = formStatusResponse.meta({
  description: "Response for get entities from library",
});

export const deleteSectionsFromLibraryRequest = z
  .object({
    // Library document _ids fed to new ObjectId(...).
    sectionIds: z.array(objectIdString).min(1).meta({
      description:
        "Library entry _ids (ObjectId 24-hex strings) to hard-delete, as returned by getEntitesFromLibrary (the entry _id). NOT the 36-char sectionId UUID used when adding.",
    }),
    resourceType: z.string().meta({
      description:
        "IGNORED by the service: deletion is scoped only by _id and portalId; resourceType is not part of the delete filter.",
    }),
  })
  .meta({
    description:
      "Permanently removes (hard deleteMany) Library entries by their document _ids, scoped to the session portalId. sectionIds must be the Library entry _ids returned by getEntitesFromLibrary (NOT the 36-char sectionId UUID used when adding). No permission check beyond portal scoping.",
  });

export const deleteSectionsFromLibraryResponse = formStatusResponse.meta({
  description: "Response for delete sections from library",
});

export const insertTemplateTypeSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "insert" (retained for request compatibility). The /insertTemplateType endpoint is a dedicated insert (formsService.insertTemplateType) — no dispatcher branch.',
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "Name of the new form-type category; rejected if a duplicate name exists in the portal.",
      }),
      resourceType: z
        .string()
        .meta({ description: "Form category this type belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
      description: zSafeHtml
        .refine((val) => val.trim().length > 0, { message: "This field is required" })
        .meta({ description: "Sanitized HTML description of the form type." }),
    }),
  })
  .meta({
    description:
      "Write: creates a new Form type category (Forms_types) in the current portal from data.name, data.resourceType and data.description. Enforces the CREATE_FORM_TEMPLATES portal permission (returns a no-permission error otherwise). Dedicated insert endpoint (formsService.insertTemplateType). Use updateTemplateType (which carries _id and requires EDIT_FORM_TEMPLATES) to edit an existing type.",
  });

export const updateTemplateTypeSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "update" (retained for request compatibility). The /updateTemplateType endpoint always updates by _id.',
    }),
    _id: objectIdString.meta({
      description:
        "Forms_type _id (ObjectId 24-hex string) to update; obtain from the formTypesController listing, not the type name.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "New name; cascaded into embedded type.name on all matching Forms templates and form instances.",
      }),
      description: zSafeHtml.meta({
        description: "New sanitized HTML description; cascaded into embedded type.description across the portal.",
      }),
    }),
  })
  .meta({
    description:
      "Write: updates an existing Form type category (Forms_types) identified by _id, setting data.name and data.description, and cascades the new name/description into embedded type fields on all matching Forms templates and InspectionAndProgress instances in the portal. Enforces the EDIT_FORM_TEMPLATES portal permission. Inputs: _id (a Forms_type id, e.g. from formTypesController list) plus data.name/description. A dedicated update endpoint (formsService.updateTemplateType); insertTemplateType handles creation.",
  });

export const deleteTemplateTypeSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "delete".' }),
    inspectionFormTypeIds: z.array(objectIdString).min(1).meta({
      description:
        "Forms_type _ids (ObjectId 24-hex strings) to soft-delete; from the formTypesController listing. Delete is blocked (nothing removed) if any non-deleted Forms template references any listed type.",
    }),
  })
  .meta({
    description:
      'Soft-deletes (sets isDeleted:true) one or more Form Type category records in the current portal, given inspectionFormTypeIds. Guarded by usage: if any non-deleted Form template references a listed type (Forms."type._id"), nothing is deleted and an errorMessage naming those Forms is returned. Get the type ids from the /formTypesController listing (templateTypesDefaultSchema). Portal scoping is automatic; no explicit permission check in this handler.',
  });

// Controller spreads req.body.value to service, so resourceType must be inside value
export const templateTypesDefaultSchema = z
  .object({
    action: z.string().optional().meta({
      description: "Dispatcher action selector; omit or send a non-mutating value for the list/default branch.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns match the form-types grid (name, description, createdDate, createdBy) — the
        // same set as the filterable allow-list — so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(templateTypesListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: templateTypesListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, description, createdDate, createdBy) plus dynamic custom-field patterns.",
        }),
        resourceType: z.string().optional().meta({
          description: "Form category to filter types by (e.g. INSPECTION); goes inside value, not at the top level.",
        }),
      })
      .optional()
      .meta({
        description: "Grid paging/filter envelope: skip, take, sorted, where, requiresCounts plus resourceType.",
      }),
  })
  .meta({
    description:
      "Read-only: lists Form type categories (Forms_types collection) for the current portal filtered by resourceType, with grid-style where/sorted/skip/take and optional total count (requiresCounts). Portal-scoped automatically; resourceType goes inside value. Returns the form-type documents used to categorise Form templates. This is the list/default action of the form-types controller; use insertTemplateType/updateTemplateType/deleteTemplateType to mutate.",
  });

export function getTemplateTypesRequestSchema(action?: string) {
  switch (action) {
    case "insert":
      return insertTemplateTypeSchema;
    case "update":
      return updateTemplateTypeSchema;
    case "delete":
      return deleteTemplateTypeSchema;
    default:
      return templateTypesDefaultSchema;
  }
}

export const getTemplateTypesResponse = formStatusResponse.meta({
  description: "Response for form types controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (getTemplateTypesRequestSchema) returns — single source of truth. The
// default (no/unknown action) variant is selected via a preprocess that maps any value
// outside the explicit action set to the "__default__" sentinel.

export const saveTemplateSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "insert" (retained for request compatibility). The /saveTemplate endpoint is a dedicated insert (formsService.saveTemplate).',
    }),
    parentFormId: objectIdString.optional().meta({
      description:
        "If this template is being duplicated from another, the source Forms template _id (ObjectId 24-hex string); its noOfDuplicatesOfthisForm counter is incremented. Omit for a brand-new template.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({ description: "Name of the new form template." }),
      resourceType: z
        .string()
        .meta({ description: "Form category the template belongs to (e.g. INSPECTION, PROGRESS_UPDATES)." }),
      type: templateTypeSchema.meta({
        description:
          "The form-type category object; type._id is a Forms_type _id from the formTypesController listing. Marked isUsedInForms=true on insert.",
      }),
      description: zSafeHtml
        .refine((val) => val.trim().length > 0, { message: "This field is required" })
        .meta({ description: "Sanitized HTML description of the template." }),
      template: z
        .array(formTemplateFieldSchema)
        .meta({ description: "The section/question structure of the template." }),
      lookUpUsed: z
        .array(z.enum(["Location", "Teams"]))
        .optional()
        .meta({
          description: "Which look-up entity sets (Location/Teams) any look-up questions in the template reference.",
        }),
    }),
  })
  .refine(
    (body) =>
      body.data.resourceType === formCategories.mailBox ||
      !(body.data.template ?? []).some((section) => section.isDefaultSection !== undefined),
    {
      error: `isDefaultSection is only allowed on ${formCategories.mailBox} form templates.`,
      path: ["data", "template"],
    },
  )
  .meta({
    description:
      "Creates a new Form template in the Forms collection (dedicated insert endpoint, no _id). Requires the CREATE_FORM_TEMPLATES portal permission. Takes data{name, resourceType, type, description, template[], lookUpUsed?}; type is a {_id,name,...} object whose _id comes from the /formTypesController listing. If parentFormId is given, increments that template's noOfDuplicatesOfthisForm. Marks the chosen Form Type as isUsedInForms:true. Use editTemplateSchema (with _id) to edit an existing template.",
  });

export const editTemplateSchema = z
  .object({
    action: z.string().meta({
      description:
        'Send "update" (retained for request compatibility). The /editTemplate endpoint always updates by _id.',
    }),
    _id: objectIdString.meta({
      description: "Forms template _id (ObjectId 24-hex string) to update; obtain from the formController listing.",
    }),
    updateName: z.boolean().optional().meta({
      description:
        "If true, allows editing even when the template is already in use (formIsInUse). Without it, editing an in-use template's content is blocked — use updateUsedTemplate for structural edits to in-use templates.",
    }),
    data: z.object({
      name: zPlainTextRequired.meta({
        description: "New template name; cascaded into embedded selectedForm.name across live instances and flows.",
      }),
      type: templateTypeSchema.meta({
        description: "New form-type object; type._id from the formTypesController listing.",
      }),
      description: zSafeHtml.meta({ description: "New sanitized HTML description; cascaded to live instances/flows." }),
      template: z.array(formTemplateFieldSchema).optional().meta({
        description:
          "New section/question structure; content edits blocked on in-use templates unless updateName is set.",
      }),
      lookUpUsed: z
        .array(z.enum(["Location", "Teams"]))
        .optional()
        .meta({
          description: "Which look-up entity sets (Location/Teams) the template's look-up questions reference.",
        }),
    }),
  })
  .meta({
    description:
      "Updates an existing Form template by _id (dedicated update endpoint). Requires the EDIT_FORM_TEMPLATES portal permission. Editing template content is blocked if the template is already in use (formIsInUse:true) unless updateName:true. On success it cascades the new name/description/type into every live InspectionAndProgress instance and formFlow that embeds this template. Get _id from the /formController listing (templateDefaultSchema) and data.type._id from /formTypesController. For structural add/edit/delete of questions on an in-use template use updateUsedTemplateSchema instead.",
  });

export const deleteTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "delete".' }),
    InspectionFormIds: z.array(objectIdString).meta({
      description:
        "Forms TEMPLATE _ids (ObjectId 24-hex strings) to soft-delete; from the formController listing. Blocked if any listed template is in use (referenced by a form instance or a project's formTemplates). Note the misleading field name — these are template ids, not instance ids.",
    }),
  })
  .meta({
    description:
      "Soft-deletes (isDeleted:true) one or more Form templates in the portal, given InspectionFormIds. Requires the DELETE_FORM_TEMPLATES portal permission. Blocked if any listed template is in use — referenced by an InspectionAndProgress instance or a project's formTemplates — returning an errorMessage listing the template names. On success, also flips each affected Form Type's isUsedInForms to false when no live Forms remain. Get template ids from /formController (templateDefaultSchema).",
  });

export const archiveTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "archive".' }),
    isArchived: z
      .boolean()
      .meta({ description: "Target archive state written to Forms.isArchived: true to archive, false to unarchive." }),
    formIds: z.array(objectIdString).meta({
      description: "Forms TEMPLATE _ids (ObjectId 24-hex strings) to (un)archive; from the formController listing.",
    }),
  })
  .meta({
    description:
      "Archives or unarchives Form templates by setting Forms.isArchived to the boolean isArchived flag for the given formIds in the portal. Requires the EDIT_FORM_TEMPLATES portal permission. This toggles a template's archive state only; it does not delete (see deleteTemplateSchema) and does not touch form instances. Get formIds from /formController (templateDefaultSchema).",
  });

// --- updateUsedTemplate `changes` (was z.any()) ---
// `changes` is an object keyed by templateEditCases; each value is an ARRAY of per-edit objects the
// service iterates. Element schemas are looseObject so the builder's dynamic/extra keys survive
// (never stripped): CONFIGURE_SECTION carries a computed key equal to `fieldType`, CONFIGURE_QUESTION
// a free `configuration` map, and the builder sends unread `sectionIndex`/`fromIndex` helpers.
// `newQuestion` reuses questionFieldSchema (which has `.catchall`) so arbitrary per-fieldType config
// survives. Every field optional — the schema must accept exactly what the (single) web builder sends
// and never tighten it, since validate-request enforces this on the real HTTP call.

const templateAddQuestionChange = z.looseObject({
  sectionId: z.string().length(36).meta({ description: "Target section _id (36-char UUID) to add the question into." }),
  questionIndex: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .meta({ description: "Insert position ($position); sent only by the duplicate path." }),
  newQuestion: questionFieldSchema.optional().meta({ description: "The full question-field object to insert." }),
});
const templateEditQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36).meta({ description: "Question _id (36-char UUID) to edit." }),
  questionValue: z.string().optional().meta({ description: "New questionValue to $set." }),
});
const templateDeleteQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36).meta({ description: "Question _id (36-char UUID) to delete." }),
});
const templateConfigureQuestionChange = z.object({
  sectionId: z.string().length(36),
  questionId: z.string().length(36),
  configuration: z
    .record(z.string(), z.unknown())
    .meta({ description: "Open map of question-field property name → new value; each key is $set on the question." }),
});
const templateConfigureSectionChange = z.looseObject({
  sectionId: z.string().length(36),
  fieldType: z.string().meta({
    description: "Names the section field to set; the new value is carried under a dynamic key equal to this string.",
  }),
});

const templateEditSectionHeadingChange = z.object({
  sectionId: z.string().length(36),
  sectionHeading: z.string().meta({ description: "New section heading to $set." }),
});

const templateDeleteSectionChange = z.object({
  sectionId: z.string().length(36).meta({ description: "Section _id (36-char UUID) to delete." }),
});

const templateChangeSectionSequenceChange = z.object({
  sectionId: z.string().length(36),
  toIndex: z.number().meta({ description: "New position ($position) for the section." }),
  fromIndex: z.number().meta({ description: "Builder helper; not read by the service." }),
});

const templateChangeQuestionSequenceChange = z.object({
  questionId: z.string().length(36),
  sectionId: z.string().length(36),
  toIndex: z.number().meta({ description: "New position ($position) for the question." }),
  fromIndex: z.number().meta({ description: "Builder helper; not read by the service." }),
});
export const templateChangesSchema = z
  .object({
    [templateEditCases.ADD_QUESTION]: z.array(templateAddQuestionChange).optional(),
    [templateEditCases.EDIT_QUESTION]: z.array(templateEditQuestionChange).optional(),
    [templateEditCases.DELETE_QUESTION]: z.array(templateDeleteQuestionChange).optional(),
    [templateEditCases.CONFIGURE_QUESTION]: z.array(templateConfigureQuestionChange).optional(),
    [templateEditCases.CONFIGURE_SECTION]: z.array(templateConfigureSectionChange).optional(),
    [templateEditCases.EDIT_SECTION_HEADING]: z.array(templateEditSectionHeadingChange).optional(),
    [templateEditCases.DELETE_SECTION]: z.array(templateDeleteSectionChange).optional(),
    [templateEditCases.CHANGE_SECTION_SEQUENCE]: z.array(templateChangeSectionSequenceChange).optional(),
    [templateEditCases.CHANGE_QUESTION_SEQUENCE]: z.array(templateChangeQuestionSequenceChange).optional(),
  })
  .meta({
    description:
      "Structural edits keyed by templateEditCases; each key maps to an array of per-edit objects. Send only the case keys you are editing (empty arrays are fine).Extra keys are preserved.",
  });

export const updateUsedTemplateSchema = z
  .object({
    action: z.string().meta({ description: 'Dispatcher action selector; send "updateUsedTemplate".' }),
    changes: templateChangesSchema.optional().meta({
      description:
        "Object keyed by templateEditCases (ADD_QUESTION, EDIT_QUESTION, DELETE_QUESTION, CONFIGURE_QUESTION, EDIT_SECTION_HEADING, CONFIGURE_SECTION, DELETE_SECTION, CHANGE_SECTION_SEQUENCE, CHANGE_QUESTION_SEQUENCE). Each key is an array of per-edit objects the service applies and cascades into every live instance.",
    }),
    _id: objectIdString.meta({
      description:
        "Forms template _id (ObjectId 24-hex string) of the in-use template to edit; from the formController listing.",
    }),
    formCategory: z.string().optional().meta({
      description: "Form category of the template (e.g. INSPECTION); scopes which live instances receive the cascade.",
    }),
  })
  .meta({
    description:
      "Applies structural edits to an already-in-use Form template (_id) and cascades them into the master Forms doc AND every live (non-deleted/rejected/approved) InspectionAndProgress instance and formFlow that uses it. Requires the EDIT_FORM_TEMPLATES portal permission. The `changes` field is validated by templateChangesSchema — an object keyed by templateEditCases (ADD_QUESTION, EDIT_QUESTION, DELETE_QUESTION, CONFIGURE_QUESTION, EDIT_SECTION_HEADING, CONFIGURE_SECTION, DELETE_SECTION, CHANGE_SECTION_SEQUENCE, CHANGE_QUESTION_SEQUENCE), each key an array of per-case edit objects (add-question/configure-section cases are looseObject so extra builder keys are preserved). Only forms with formIsInUse:true are matched. Use editTemplateSchema for name/type edits on not-yet-used templates.",
  });

// Controller spreads req.body.value and adds req.body.formId to service
// Service uses: params.formId, params.isArchived, params.resourceType + grid fields
export const templateDefaultSchema = z
  .object({
    action: z
      .string()
      .optional()
      .meta({ description: "Dispatcher action selector; omit for the list/default (read) branch." }),
    formId: z
      .union([objectIdString, z.array(objectIdString)])
      .optional()
      .meta({
        description:
          "Fetch specific Forms TEMPLATE(s) by _id — a single ObjectId 24-hex string or an array of them. Omit to list templates via the grid. Ids come from a prior formController listing.",
      }),
    // Some callers (e.g. getUpdatedTemplate) send isArchived at the top level; the controller
    // reads it off `value`, so a top-level flag is accepted here for type-compatibility.
    isArchived: z.boolean().optional().meta({
      description:
        "Top-level archive filter accepted for compatibility, but the service reads value.isArchived — prefer setting it inside value.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns (name, description, type.name, createdDate, createdBy.fullName) are a subset
        // of the filterable allow-list, so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(templatesListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: templatesListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, description, type.name, createdDate, createdBy.fullName, _id, isArchived, projectId, type._id) plus dynamic custom-field patterns.",
        }),
        resourceType: z
          .string()
          .optional()
          .meta({ description: "Form category to filter templates by (e.g. INSPECTION)." }),
        isArchived: z
          .boolean()
          .optional()
          .meta({ description: "Filter by archive state: true = archived only, false = active only." }),
        projectId: objectIdString.optional().meta({
          description:
            "If set, returns only templates attached to this Project (ObjectId 24-hex string) and enforces the project-level CONFIGURATION_PROJECT_VIEW_LIST_OF_FORM_TEMPLATES permission instead of the portal permission.",
        }),
      })
      .optional()
      .meta({
        description:
          "Grid paging/filter envelope: skip, take, sorted, where, requiresCounts plus resourceType/isArchived/projectId.",
      }),
  })
  .meta({
    description:
      "Lists Form templates (the Forms collection) for the portal, filtered by resourceType and grid where/sorted/skip/take, or fetches specific template(s) when formId (single id or array) is supplied. If value.projectId is set it returns only templates attached to that Project and enforces the CONFIGURATION_PROJECT_VIEW_LIST_OF_FORM_TEMPLATES project permission; otherwise it enforces the VIEW_LIST_OF_FORM_TEMPLATES portal permission. This is the read/list endpoint — use saveTemplate/editTemplate to mutate.",
  });

export function getTemplatesRequestSchema(action?: string) {
  switch (action) {
    case "insert":
      return saveTemplateSchema;
    case "update":
      return editTemplateSchema;
    case "delete":
      return deleteTemplateSchema;
    case "archive":
      return archiveTemplateSchema;
    case "updateUsedTemplate":
      return updateUsedTemplateSchema;
    default:
      return templateDefaultSchema;
  }
}

export const getTemplatesResponse = extendStatusResponse({
  result: z.array(z.any()).optional(),
  count: z.number().nonnegative().optional(),
}).meta({
  description: "Response for form controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (getTemplatesRequestSchema) returns — single source of truth. The
// updateUsedTemplate variant's `changes: z.any()` is narrowed to z.unknown() so the
// union is JSON-Schema representable for advertising; HTTP validation is unaffected.

// Plain-object inner schema (no `.refine`) so it can be a `discriminatedUnion` member in
// taskInspectionAndProgressFormsControllerRequestUnion below. `tipfUpdateSchema` derives
// from this and adds the cross-field refinement — single source of truth for the fields.
const tipfUpdateInnerSchema = z.object({
  action: z.literal("update").meta({ description: 'Dispatcher action selector; must be the literal "update".' }),
  _id: objectIdString.optional().meta({
    description:
      "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) to mutate; from the taskInspectionAndProgressFormsController listing. Either _id or formId is required.",
  }),
  formId: objectIdString.optional().meta({
    description:
      "Alternative to _id: the same form-instance identifier. Either _id or formId is required (both accepted).",
  }),
  data: z.object({
    // NOTE: within one request the service executes only the FIRST matching operation (rollback,
    // level-submit, submit, approve, reject, section-verify, then the section-specific handlers).
    // Send fields for a single intended operation per call.
    rollBack: z
      .boolean()
      .optional()
      .meta({ description: "If true, roll the form back one approval level (decrements currentLevelOfForm)." }),
    SES: z.string().optional().meta({
      description:
        "SAP Service Entry Sheet number; when set during final-level section verification it is written to the form's SES_NO field. Only consumed on the verification path.",
    }),
    editedLevel: formFlowLevelSchema.optional().meta({
      description:
        "A modified approval-level definition applied to the next level during submit/approve (dynamic-user reconfiguration).",
    }),
    autoVerifyAllSections: z.boolean().optional().meta({
      description:
        "If true, treat all sections as verified so the form can be approved without per-section verification.",
    }),
    config: z
      .object({
        wbsItemId: objectIdString.meta({
          description: "contractsHasWBSItems item id whose quantity/amount is being set.",
        }),
        boqItemId: objectIdString.optional().meta({ description: "Optional BOQ item id under the WBS item." }),
        quantity: z.number().nonnegative().optional().meta({ description: "Quantity to record for the item." }),
        amount: z.number().nonnegative().optional().meta({ description: "Amount to record for the item." }),
        progressUpdateId: objectIdString
          .optional()
          .meta({ description: "Progress-update context id, when the form is a progress update." }),
        inspectionId: objectIdString
          .optional()
          .meta({ description: "Inspection context id, when the form is an inspection." }),
        remark: zSafeHtml
          .optional()
          .nullable()
          .meta({ description: "Optional sanitized-HTML remark for the item entry." }),
      })
      .optional()
      .meta({
        description:
          "Item-value payload used by the updateItemFields WBS operation; only read on the WBS-items section path.",
      }),
    updateItemFields: z.boolean().optional().meta({
      description:
        "If true (with wbsItemsSectionId + config), updates the WBS item's recorded quantity/amount fields on the form.",
    }),
    attachWBSItemsToForm: z
      .object({
        boqItemIds: z
          .array(objectIdString)
          .optional()
          .meta({ description: "BOQ item ids to attach under the WBS item." }),
        wbsItemId: objectIdString.meta({ description: "contractsHasWBSItems item id to attach to the form." }),
      })
      .optional()
      .meta({
        description: "Attaches a WBS item (and optional BOQ items) to the form; only on the WBS-items section path.",
      }),
    removeWBSItemsFromForm: z
      .object({
        wbsItemIds: z.array(objectIdString).optional().meta({ description: "WBS item ids to detach from the form." }),
      })
      .optional()
      .meta({ description: "Detaches WBS items from the form; only on the WBS-items section path." }),
    reviewsSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Reviews preset section; presence routes/permission-gates the attach-reviews operation.",
    }),
    attachedReviews: z
      .array(
        z.object({
          _id: objectIdString.meta({ description: "Review _id (ObjectId 24-hex string) to attach to the form." }),
        }),
      )
      .optional()
      .meta({ description: "Reviews to attach to the form (with reviewsSectionId)." }),
    wbsItemsSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the WBS-items preset section; presence routes/permission-gates WBS-item operations.",
    }),
    tableSectionDataSource: z
      .array(z.any())
      .optional()
      .meta({ description: "New rows/grid data for a table section (with tableSectionId)." }),
    tableSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the table section to update; presence + tableSectionDataSource routes the table-update operation.",
    }),
    referencesSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the References preset section; presence routes/permission-gates attaching reference file versions.",
    }),
    mileStoneSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Milestones preset section; presence routes/permission-gates milestone operations.",
    }),
    mileStoneId: objectIdString.optional().meta({
      description:
        "Target mileStones _id (ObjectId 24-hex string) for updateMilestoneAmount / updateMileStoneDate operations.",
    }),
    completionDate: z.number().nullable().optional().meta({
      description:
        "Epoch ms. DEAD/unread input — no milestone handler consumes the top-level completionDate; milestone completion dates are set only via updateMileStoneDate.completionDate (with mileStoneId).",
    }),
    attachMilestones: z.array(objectIdString).optional().meta({ description: "Milestone _ids to attach to the form." }),
    removeMilestones: z
      .array(objectIdString)
      .optional()
      .meta({ description: "Milestone _ids to detach from the form." }),
    updateMileStoneDate: z
      .object({
        completionDate: z.number().meta({ description: "New completion date as epoch ms." }),
      })
      .optional()
      .meta({ description: "Sets a milestone's completion date (requires mileStoneId)." }),
    updateMilestoneAmount: z
      .object({
        amountInForm: z.number().nonnegative().meta({ description: "Amount recorded for the milestone in this form." }),
      })
      .optional()
      .meta({ description: "Sets a milestone's amount in the form (requires mileStoneId)." }),
    addBoqItems: z
      .object({
        boqItems: z
          .array(
            z.object({
              customQuantity: z
                .number()
                .nonnegative()
                .meta({ description: "Quantity to reserve/record for this BOQ item." }),
              itemId: objectIdString.meta({ description: "contractsHasBoqItems item id to add." }),
            }),
          )
          .meta({ description: "BOQ items to add to the form's Quantities section." }),
        autoPopulateAllAvailableQuantity: z.boolean().meta({
          description: "If true, auto-fills each item's full available quantity instead of using customQuantity.",
        }),
      })
      .optional()
      .meta({ description: "Adds BOQ items to the Quantities preset section (with quantitiesSectionId)." }),
    customQuantityData: z
      .object({
        value: z.number().nonnegative().meta({ description: "The custom quantity value to record." }),
        itemId: objectIdString.meta({ description: "BOQ item id the quantity applies to." }),
        originalTaskId: objectIdString
          .optional()
          .meta({ description: "Task id context when recording quantity against a task." }),
        inspectionId: objectIdString
          .optional()
          .nullable()
          .meta({ description: "Inspection context id, if applicable." }),
        progressUpdateId: objectIdString
          .optional()
          .nullable()
          .meta({ description: "Progress-update context id, if applicable." }),
      })
      .optional()
      .meta({
        description: "Records a custom quantity for a single BOQ item; used on the Quantities and Tasks section paths.",
      }),
    taskConfig: z
      .object({
        originalTaskId: objectIdString.meta({
          description: "contractsHasTasks originalTaskId whose progress fields are being set.",
        }),
        progress: z
          .number()
          .min(0)
          .max(100)
          .optional()
          .nullable()
          .meta({ description: "Percent complete, 0-100; null to clear." }),
        remDuration: z.number().optional().nullable().meta({ description: "Remaining duration; null to clear." }),
        actualEndDate: z
          .number()
          .optional()
          .nullable()
          .meta({ description: "Actual end date as epoch ms; null to clear." }),
        actualStartDate: z
          .number()
          .optional()
          .nullable()
          .meta({ description: "Actual start date as epoch ms; null to clear." }),
      })
      .optional()
      .meta({ description: "Sets progress/dates for one attached task; only on the Tasks section path." }),
    tasksSectionId: z.string().optional().meta({
      description: "36-char UUID of the Tasks preset section; presence routes/permission-gates task operations.",
    }),
    removeTasks: z.array(objectIdString).optional().meta({ description: "Task ids to detach from the form." }),
    attachTasks: z.array(objectIdString).optional().meta({ description: "Task ids to attach to the form." }),
    referenceFileVersionIds: z
      .array(objectIdString)
      .optional()
      .meta({ description: "files_properties file-version ids to attach as references (with referencesSectionId)." }),
    quantitiesSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Quantities preset section; presence routes/permission-gates BOQ quantity operations.",
    }),
    removeBoqItems: z
      .array(objectIdString)
      .optional()
      .meta({ description: "BOQ item ids to remove from the Quantities section." }),
    isSubmitted: z.boolean().optional().meta({
      description:
        "If true and at the final submission level, submits the form (status SUBMITTED); requires actionComment context.",
    }),
    isRejected: z
      .boolean()
      .optional()
      .meta({ description: "If true, reject the form (status REJECTED, reverts quantities). Requires actionComment." }),
    actionComment: zSafeHtml.optional().meta({
      description:
        "Sanitized-HTML comment stored with a submit/approve/reject/rollback action; required for approve and reject.",
    }),
    actionSignDoc: z.string().optional().nullable().meta({
      description:
        "Signature document: an S3 key/base64 stream, or omitted when actionSignType is IMPORT_PROFILE_SIGNATURE.",
    }),
    actionSignType: z.string().optional().meta({
      description: "Signature mode, e.g. IMPORT_PROFILE_SIGNATURE, controlling how actionSignDoc is handled.",
    }),
    isApproved: z.boolean().optional().meta({
      description:
        "If true (form submitted + all sections verified), approve the form (status APPROVED, commits quantities). Requires actionComment.",
    }),
    sectionsVerified: z.array(z.string()).optional().meta({
      description:
        "Section 36-char UUIDs being marked verified at the final level; drives the section-verification operation.",
    }),
    proceedToNextLevel: z.boolean().optional().meta({
      description: "If true and below the final level, advance the form to the next approval level (level submit).",
    }),
    sectionId: z
      .string()
      .optional()
      .meta({ description: "36-char UUID of the section containing the question being answered (answer-fill path)." }),
    questionId: z
      .string()
      .optional()
      .meta({ description: "36-char UUID of the question being answered (answer-fill path)." }),
    answer: z.unknown().optional().meta({
      description: "The answer value to write for sectionId/questionId; shape depends on the question's fieldType.",
    }),
    addFileUploadFieldData: z
      .object({
        fileBase64Str: z.string().meta({
          description:
            "Base64-encoded file content written to storage. No size limit is enforced by the schema (large payloads risk OOM / 16MB BSON).",
        }),
        awsKey: z.string().meta({ description: "S3 object key to store the file under." }),
      })
      .optional()
      .meta({ description: "Adds a file to a fileUpload question's answer." }),
    removeFileUploadFieldData: z
      .object({
        tableRowIndex: z
          .number()
          .optional()
          .meta({ description: "Row index when the fileUpload field is inside a table section." }),
        fileIndex: z.number().meta({ description: "Index of the file to remove within the field's file list." }),
      })
      .optional()
      .meta({ description: "Removes one file from a fileUpload question's answer." }),
    linkFormSectionId: z.string().optional().meta({
      description:
        "36-char UUID of the Linked-Forms preset section; presence routes/permission-gates linking other forms.",
    }),
    linkedForms: z
      .array(linkedFormSchema)
      .optional()
      .meta({ description: "Other form instances to link into this form (with linkFormSectionId)." }),
    attachmentsData: z
      .object({
        name: zPlainTextRequired.meta({ description: "Display file name of the attachment being added." }),
        type: z.string().optional().meta({ description: "MIME type of the attachment." }),
      })
      .optional()
      .meta({ description: "Metadata for an attachment added via addAttachment/base64String." }),
    base64String: z
      .string()
      .optional()
      .meta({ description: "Base64 content of an attachment to add (with addAttachment). No schema size limit." }),
    addAttachment: z
      .boolean()
      .optional()
      .meta({ description: "If true, add the attachment described by attachmentsData/base64String to the form." }),
    attachmentIndex: z.number().optional().meta({
      description:
        "Accepted but NOT read by any handler (dead input) — add/remove attachment operations key on addAttachment / removeAttachmentAwsKey, not on an index.",
    }),
    removeAttachmentAwsKey: z
      .string()
      .optional()
      .meta({ description: "S3 key of an existing attachment to remove from the form." }),
  }),
});

export const tipfUpdateSchema = tipfUpdateInnerSchema
  .refine(
    (data) => {
      return data._id || data.formId;
    },
    {
      error: "Either _id or formId must be provided",
    },
  )
  .meta({
    description:
      "The multi-purpose mutation for a single form instance (InspectionAndProgress), identified by _id or formId (at least one required). Runs inside a MongoDB transaction. Depending on the fields in `data`, it fills a question answer (sectionId/questionId/answer), verifies sections, submits/approves/rejects/rolls back through the form's approval levels, and attaches/removes WBS items, BOQ items, tasks, milestones, reviews, linked forms, and file uploads. Action-level permission is enforced inline against the instance's selectedForm.template permissions at the current level (approve/reject require actionComment). Get _id/formId from /taskInspectionAndProgressFormsController (tipfDefaultSchema).",
  });

export const tipfSaveSchema = z
  .object({
    action: z.literal("save").meta({ description: 'Dispatcher action selector; must be the literal "save".' }),
    data: z.object({
      title: zPlainTextRequired
        .pipe(z.string().min(1).max(500))
        .meta({ description: "Title of the new form instance (1-500 chars)." }),
      rememberUserSettings: z.boolean().meta({
        description:
          "If true, saves this form's settings under the user's FORM_SETTINGS so it can be quick-created later via createFormFromUserSettings.",
      }),
      description: zSafeHtml
        .pipe(z.string().max(5000))
        .optional()
        .meta({ description: "Optional sanitized-HTML description (max 5000 chars)." }),
      dueDate: z.number().optional().nullable().meta({ description: "Optional due date as epoch ms; null for none." }),
      resourceType: z.string().meta({
        description:
          "Form category (e.g. INSPECTION, PROGRESS_UPDATES); also the space-permission name checked for create rights.",
      }),
      selectedTemplate: objectIdString.meta({
        description:
          "Forms TEMPLATE _id (ObjectId 24-hex string) to instantiate; from the formController listing. Its full template is copied into the new instance.",
      }),
      formFlow: z
        .array(formFlowLevelSchema)
        .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) for this instance." }),
      attachedProgressUpdates: z.array(z.any()).optional().meta({
        description:
          "Source progress-update form-instance ids (ObjectId strings) this form is derived from. If non-empty, the form becomes basedOnProgressUpdates and quantities are transferred from those forms. Takes precedence over attachedInspections.",
      }),
      attachedInspections: z.array(z.any()).optional().meta({
        description:
          "Source inspection form-instance ids (ObjectId strings) this form is derived from. If non-empty (and attachedProgressUpdates is empty), the form becomes basedOnInspections.",
      }),
      spaceId: objectIdString.meta({
        description:
          "Space _id (ObjectId 24-hex string) the form is created in; create-permission is checked here. From getSpacesList.",
      }),
      contractId: objectIdString.optional().meta({
        description:
          "Contract _id (ObjectId 24-hex string) for contract-based forms; requires the contract locked, caller has modify permission, module enabled, and not in draft (except changeOrder). Omit for non-contract forms.",
      }),
      contractVersionId: objectIdString.optional().meta({
        description:
          "On a normal form the persisted contractVersionId is overwritten with the contract's latest version (input does not affect it). BUT on the changeOrder path the raw input IS read to select which contractsVersions doc receives changeOrderId — a wrong value targets the wrong version and omitting it matches none.",
      }),
      basedOnTasks: z.boolean().optional().meta({
        description:
          "IGNORED on save: the service hardcodes basedOnTasks=true then derives it from attachedInspections/attachedProgressUpdates; the input value is not read.",
      }),
      basedOnInspections: z.boolean().optional().meta({
        description:
          "The PERSISTED basedOnInspections is derived server-side (set true when attachedInspections is non-empty), but the INPUT flag IS read: source-form quantity transfer runs only when attachedInspections AND basedOnInspections are both truthy — sending it false/omitting it suppresses the transfer.",
      }),
      basedOnBudget: z.boolean().optional().meta({
        description:
          "IGNORED on save: the service hardcodes basedOnBudget=true unless attached* arrays override it; the input value is not read.",
      }),
      basedOnProgressUpdates: z.boolean().optional().meta({
        description:
          "The PERSISTED basedOnProgressUpdates is derived server-side (set true when attachedProgressUpdates is non-empty), but the INPUT flag IS read: source-form quantity transfer runs only when attachedProgressUpdates AND basedOnProgressUpdates are both truthy — sending it false/omitting it suppresses the transfer.",
      }),
      parentContract: z.string().optional().nullable().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.parentId) for contract forms; the input value is overwritten.",
      }),
      allParentContracts: z.array(objectIdString).optional().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.allParents); the input value is overwritten.",
      }),
      allChildContracts: z.array(objectIdString).optional().meta({
        description:
          "IGNORED on save: derived from the contract (contractData.allChilds); the input value is overwritten.",
      }),
      projectId: objectIdString.meta({
        description: "Project _id (ObjectId 24-hex string) the form belongs to; from the projects listing.",
      }),
      selectedFormFlow: z
        .object({
          name: zPlainTextRequired.meta({
            description: "Name of the reusable form-flow template this instance is based on.",
          }),
          _id: objectIdString.meta({
            description: "formFlow template _id (ObjectId 24-hex string) this instance is based on.",
          }),
        })
        .optional()
        .meta({ description: "Optional reference to the reusable formFlow template used; recorded on the instance." }),
    }),
  })
  .meta({
    description:
      "Creates a new Form instance (an InspectionAndProgress record) in a Space from a selected Form template and its approval formFlow. Enforces Space create-permission for the given resourceType via checkCreatePermissionInSpace; for Contract-based forms it additionally requires the Contract to be locked, the caller to hold contract modify permission, the module enabled, and the contract not in draft (except changeOrder). Requires spaceId (getSpacesList) + projectId (projects_getIncludedProjectsController) + resourceType + selectedTemplate (a Forms template _id). Runs in a MongoDB transaction. Distinct from formFlow save, which stores a reusable flow template, not a live form.",
  });

export const tipfDeleteSchema = z
  .object({
    action: z.literal("delete").meta({ description: 'Dispatcher action selector; must be the literal "delete".' }),
    resourceType: z.string().meta({
      description: "Form category (e.g. INSPECTION) the listed instances belong to; part of the delete match filter.",
    }),
    resourceIds: z.array(objectIdString).meta({
      description:
        "InspectionAndProgress form-INSTANCE _ids (ObjectId 24-hex strings) to soft-delete; from the form list. Only instances created by the calling user are deleted, and only if not referenced as a dependency by other forms.",
    }),
  })
  .meta({
    description:
      "Soft-deletes Form instances by resourceIds (sets isDeleted). Only forms created by the calling user (createdBy._id must match sessionUserId) and matching the given resourceType/portal are deleted, and only if the form is NOT referenced as a dependency by other forms (via attachedInspections/attachedProgressUpdates) — dependency forms are skipped. For Contract-based forms it also releases the reserved BOQ/WBS quantities. Runs in a transaction. resourceIds are InspectionAndProgress form _ids from the form list (taskInspectionAndProgressFormsController default/list action).",
  });

export const tipfArchiveSchema = z
  .object({
    action: z.literal("archive").meta({ description: 'Dispatcher action selector; must be the literal "archive".' }),
    resourceIds: z.array(objectIdString).meta({
      description:
        "InspectionAndProgress form-INSTANCE _ids (ObjectId 24-hex strings) to (un)archive; from the form list. Matched only by portal — no per-form owner check.",
    }),
    data: z.object({
      isArchived: z.object({
        status: z.boolean().meta({ description: "Target archive state: true to archive, false to restore." }),
      }),
    }),
  })
  .meta({
    description:
      "Archives or un-archives existing Form instances by writing isArchived.status on each matched InspectionAndProgress record; pass data.isArchived.status=true to archive, false to restore. Matches only forms in the caller's portal; there is no per-form owner/role check beyond portal scope. resourceIds are form _ids from the form list. Does not delete — use tipfDeleteSchema for that.",
  });

const tipfUpdatePermissionSchema = z
  .object({
    action: z.literal("updatePermission").meta({
      description:
        'Dispatcher action selector; the literal "updatePermission". (Advertise-only: the live dispatcher does not handle this action.)',
    }),
    role: z.union([z.literal(4), z.literal(6), z.literal(7)]).meta({
      description:
        "Numeric folder-permission role id to assign the user on the form (e.g. 4/6/7 = viewer/editor/admin tiers).",
    }),
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-instance _id (ObjectId 24-hex string) whose user permission is being changed.",
    }),
    userId: objectIdString.meta({
      description: "Account _id (ObjectId 24-hex string) of the user whose role on the form is updated.",
    }),
  })
  .meta({
    description: "Payload for task inspection/progress permission update",
  });

export const tipfDefaultSchema = z
  .object({
    resourceId: objectIdString.optional().meta({
      description: "If set, fetch a single form INSTANCE by its _id (ObjectId 24-hex string) instead of listing.",
    }),
    value: z
      .object({
        skip: z.number().nonnegative().optional().meta({ description: "Pagination offset (rows to skip)." }),
        take: z.number().nonnegative().optional().meta({ description: "Pagination page size (max rows)." }),
        sorted: tipfListSorted,
        where: tipfListWhere.meta({
          description:
            "Grid filter clause over allowed columns (counter, title, status, dueDate, space.spaceName, selectedForm.*, createdBy.fullName, lastAction.*, currentLevelOfForm, isArchived.status, isApproved, _id) plus dynamic custom-field patterns.",
        }),
        isActionable: z.boolean().optional().meta({
          description: "If true, restrict to instances the user can currently act on (IN_PROGRESS, unarchived).",
        }),
        resourceType: z.string().optional().meta({ description: "Form category filter (e.g. INSPECTION)." }),
        projectId: objectIdString
          .optional()
          .meta({ description: "Restrict to instances in this Project (ObjectId 24-hex string)." }),
        contractId: objectIdString
          .optional()
          .meta({ description: "Restrict to instances on this Contract (ObjectId 24-hex string)." }),
        selectedProjectIds: z
          .array(objectIdString)
          .optional()
          .meta({ description: "Restrict to instances across multiple Projects (array of ObjectId 24-hex strings)." }),
        requiresCounts: z
          .boolean()
          .optional()
          .meta({ description: "If true, also return the total matching-instance count." }),
        requiresActionableCount: z
          .boolean()
          .optional()
          .meta({ description: "If true, also return the count of instances the user can currently act on." }),
      })
      .optional()
      .meta({ description: "Grid paging/filter envelope for listing form instances." }),
  })
  .meta({
    description:
      "Lists form instances (the InspectionAndProgress collection — actual filled forms, not templates) that the calling user is permitted to view, scoped to the portal and to instances whose users[] includes the session user with RESOURCE_VIEW permission. Supports grid where/sorted/skip/take, resourceType/projectId/contractId filters, isActionable (only IN_PROGRESS, unarchived), and count flags; pass resourceId to fetch one instance by id. This is the read/list endpoint — use updateTaskInspectionAndProgress (tipfUpdate) or save/delete/archive to mutate.",
  });

export function taskInspectionAndProgressFormsControllerRequestSchema(action?: string) {
  switch (action) {
    case "update":
      return tipfUpdateSchema;
    case "save":
      return tipfSaveSchema;
    case "delete":
      return tipfDeleteSchema;
    case "archive":
      return tipfArchiveSchema;
    case "updatePermission":
      return tipfUpdatePermissionSchema;
    default:
      return tipfDefaultSchema;
  }
}

export const taskInspectionAndProgressFormsControllerResponse = extendStatusResponse({
  result: z.array(z.any()).optional(),
  count: z.number().nonnegative().optional(),
  lastSaved: z.string().optional(),
}).meta({
  description: "Response for task inspection/progress forms controller",
});

// Discriminated union over the SAME per-action schemas the dispatcher
// (taskInspectionAndProgressFormsControllerRequestSchema) returns — single source of truth.
// The update variant uses tipfUpdateInnerSchema (the plain object `tipfUpdateSchema` derives
// from) because discriminatedUnion members must be plain objects, not `.refine`-wrapped ones.
// The default (no/unknown action) variant is selected via a preprocess that maps any value
// outside the explicit action set to the "__default__" sentinel. This is advertise-only:
// HTTP validation remains the dispatcher (which dispatches on
// `req.body.action || req.body?.value?.action` and does NOT itself handle `updatePermission`).

export const formFlowUpdateSchema = z
  .object({
    action: z.literal("update").meta({
      description:
        'Must be the literal "update" (retained for request compatibility); the /updateFormFlow endpoint edits the flow in place by _id.',
    }),
    _id: objectIdString.meta({
      description: "formFlow template _id (ObjectId 24-hex string) to update; from the formFlowController listing.",
    }),
    data: z.object({
      name: zPlainText.optional().meta({ description: "New flow name (patched only if provided)." }),
      projectId: objectIdString.meta({
        description:
          "Project _id (ObjectId 24-hex string) the flow belongs to; the SPECIFIC_CREATE_FORM_FLOW permission is checked against it. From the projects listing.",
      }),
      formFlow: z
        .array(formFlowLevelSchema)
        .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) defining the flow." })
        .optional(),
    }),
  })
  .meta({
    description:
      "Updates an existing reusable form-flow template (formFlow collection) identified by _id, patching only the supplied fields (name, formFlow levels, and dotted selectedForm.template/name/description keys). Requires the SPECIFIC_CREATE_FORM_FLOW project-level permission (checkProjectLevelPermission on data.projectId). A dedicated update endpoint (formsService.updateFormFlow) that edits in place by _id; formFlowSaveSchema (/saveFormFlow) inserts a new flow. _id from the formFlow list (formFlowController); projectId from projects_getIncludedProjectsController.",
  });

export const formFlowSaveSchema = z.object({
  action: z.literal("save").meta({
    description:
      'Must be the literal "save" (retained for request compatibility); the /saveFormFlow endpoint always inserts a new flow.',
  }),
  data: z.object({
    name: zPlainTextRequired.meta({ description: "Name of the new form-flow template." }),
    formCategory: z
      .string()
      .meta({ description: "Form category the flow is for (e.g. INSPECTION, PROGRESS_UPDATES)." }),
    selectedTemplate: objectIdString,
    spaceId: objectIdString.meta({
      description: "Space _id (ObjectId 24-hex string); caller must be a member. From getSpacesList.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string); SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
    userSelectionMode: z.enum(["allSpaceUsers", "currentUser"]).optional(),
    formFlow: z
      .array(formFlowLevelSchema)
      .optional()
      .meta({ description: "The approval levels (Fill/Modify/Verify grids + allowed users) defining the flow." }),
  }),
});

export const formFlowDuplicateSchema = z
  .object({
    action: z.literal("duplicate").meta({
      description:
        'Dispatcher action selector; must be the literal "duplicate" (always duplicates an existing form flow).',
    }),
    sourceFormFlowId: objectIdString.meta({
      description:
        "If duplicating from an existing flow, its _id (ObjectId 24-hex string); that parent's noOfDuplicates counter is incremented. Omit for a brand-new flow.",
    }),
  })
  .meta({
    description: "Duplicates the exising formFlow",
  });

export const formFlowDeleteSchema = z
  .object({
    action: z.literal("delete").meta({ description: 'Dispatcher action selector; must be the literal "delete".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) to soft-delete; from the formFlowController listing.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) scoping the delete; SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Soft-deletes reusable form-flow templates by formFlowIds, scoped to the given projectId (sets isDeleted on matching formFlow docs). Requires the SPECIFIC_CREATE_FORM_FLOW project permission. After deletion, for each referenced Form template it clears Forms.formIsInUse when no other live formFlow or InspectionAndProgress form still uses that template. Targets flow templates, not live form instances (that is tipfDeleteSchema). formFlowIds from the formFlow list; projectId from projects_getIncludedProjectsController.",
  });

export const formFlowAddToLibrarySchema = z
  .object({
    action: z
      .literal("addToLibrary")
      .meta({ description: 'Dispatcher action selector; must be the literal "addToLibrary".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) in the caller's portal to copy into the Library as FORM_FLOW entries; from the formFlowController listing. Flows missing a project/space are skipped.",
    }),
  })
  .meta({
    description:
      "Copies existing form-flow templates (by formFlowIds, within the caller's portal) into the reusable Library collection as FORM_FLOW entries, upserting one Library record per flow (keyed by data.formFlowId) capturing its selectedForm, formCategory, formFlow, project and space. No explicit role/permission check beyond portal scoping. Flows missing a project/space are skipped. formFlowIds from the formFlow list. Pair with formFlowAddFromLibrarySchema to instantiate library flows back into a Space.",
  });

export const formFlowAddFromLibrarySchema = z
  .object({
    action: z
      .literal("addFromLibrary")
      .meta({ description: 'Dispatcher action selector; must be the literal "addFromLibrary".' }),
    usersData: z
      .array(
        z.object({
          id: objectIdString.meta({
            description:
              "Account _id (ObjectId 24-hex string) to place into every level's allowed submit/approve/reject/rollback user lists.",
          }),
          name: zPlainTextRequired.meta({ description: "Display name of the user (denormalized into the flow)." }),
        }),
      )
      .meta({
        description:
          "Users that REPLACE each instantiated level's allowed-users lists; the library flow's original users are discarded.",
      }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "Library FORM_FLOW entry _ids (ObjectId 24-hex strings) to instantiate — here these are Library document _ids (from getEntitesFromLibrary), not live formFlow ids.",
    }),
    spaceId: objectIdString.meta({
      description: "Target Space _id (ObjectId 24-hex string); caller must be a member. From getSpacesList.",
    }),
    projectId: objectIdString.meta({
      description:
        "Target Project _id (ObjectId 24-hex string); SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Instantiates one or more Library FORM_FLOW entries (by formFlowIds, which here are Library document _ids) into a target Space/Project as new formFlow templates, replacing each level's allowed submit/approve/reject/rollback users with the supplied usersData. Requires the SPECIFIC_CREATE_FORM_FLOW project permission AND that the caller belongs to the Space. spaceId from getSpacesList, projectId from projects_getIncludedProjectsController, usersData ids are account _ids. Reverse of formFlowAddToLibrarySchema.",
  });

export const formFlowArchiveSchema = z
  .object({
    action: z.literal("archive").meta({ description: 'Dispatcher action selector; must be the literal "archive".' }),
    formFlowIds: z.array(objectIdString).meta({
      description:
        "formFlow template _ids (ObjectId 24-hex strings) to (un)archive; from the formFlowController listing.",
    }),
    isArchived: z.boolean().meta({
      description: "Target archive state written to formFlow.isArchived: true to archive, false to unarchive.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) scoping the operation; SPECIFIC_CREATE_FORM_FLOW is checked against it. From the projects listing.",
    }),
  })
  .meta({
    description:
      "Archives or unarchives reusable form-flow templates (formFlow collection) by setting formFlow.isArchived to the boolean isArchived flag for the given formFlowIds scoped to projectId. Requires the SPECIFIC_CREATE_FORM_FLOW project permission. This toggles a flow's archive state only; it does not delete (see formFlowDeleteSchema). formFlowIds from the formFlow list; projectId from projects_getIncludedProjectsController.",
  });

// Service uses: params.formFlowId, params.value.{sorted, projectId, where, spaceId, selectedFormId, formCategory, skip, take, requiresCounts}
export const formFlowDefaultSchema = z
  .object({
    action: z
      .string()
      .optional()
      .meta({ description: "Dispatcher action selector; omit for the list/default (read) branch." }),
    projectId: objectIdString.optional().meta({
      description: "Top-level Project filter (ObjectId 24-hex string); the service also reads value.projectId.",
    }),
    formFlowId: objectIdString.optional().meta({
      description: "If set, fetch a single formFlow template by _id (ObjectId 24-hex string) instead of listing.",
    }),
    value: gridValueSchema
      .extend({
        // Sortable columns (name, selectedForm, space.spaceName, createdBy, createdDate) are a subset
        // of the filterable allow-list, so reuse it. Overrides the open base `sorted`.
        sorted: sortConfigFor(formFlowListFilterableFields, { patterns: dynamicCustomFieldPatterns }),
        where: formFlowListWhere.meta({
          description:
            "Grid filter clause over allowed columns (name, selectedForm, space.spaceName, createdBy, createdDate, _id) plus dynamic custom-field patterns.",
        }),
        projectId: objectIdString
          .optional()
          .meta({ description: "Restrict flows to this Project (ObjectId 24-hex string)." }),
        spaceId: objectIdString
          .optional()
          .meta({ description: "Restrict flows to this Space (ObjectId 24-hex string). From getSpacesList." }),
        selectedFormId: z
          .string()
          .optional()
          .meta({ description: "Restrict flows built on this Forms template _id (ObjectId 24-hex string)." }),
        formCategory: z
          .string()
          .optional()
          .meta({ description: "Restrict flows to this form category (e.g. INSPECTION)." }),
        isArchived: z.boolean().optional().meta({
          description:
            "Archive filter: true = archived flows only; omitted/false = active (non-archived) flows only. Legacy flows without the field are treated as active.",
        }),
      })
      .optional()
      .meta({ description: "Grid paging/filter envelope for listing form-flow templates." }),
  })
  .meta({
    description:
      "Lists reusable form-flow templates (formFlow collection) for the portal, scoped to Spaces the caller belongs to; supports skip/take/sorted/where paging plus optional projectId, spaceId, selectedFormId and formCategory filters. If formFlowId is supplied it returns that single flow document instead. This is read-only; use formFlow save/update to create or modify a flow. projectId from projects_getIncludedProjectsController, spaceId from getSpacesList.",
  });

export function formFlowControllerRequestSchema(action?: string) {
  switch (action) {
    case "update":
      return formFlowUpdateSchema;
    case "save":
      return formFlowSaveSchema;
    case "delete":
      return formFlowDeleteSchema;
    case "addToLibrary":
      return formFlowAddToLibrarySchema;
    case "addFromLibrary":
      return formFlowAddFromLibrarySchema;
    case "archive":
      return formFlowArchiveSchema;
    default:
      return formFlowDefaultSchema;
  }
}

export const formFlowControllerResponse = extendStatusResponse({
  result: z.array(z.any()).optional(),
  count: z.number().nonnegative().optional(),
}).meta({
  description: "Response for form flow controller",
});

// Discriminated union composed from the SAME per-action schemas the dynamicSchema
// dispatcher (formFlowControllerRequestSchema) returns — single source of truth. Every
// explicit variant already pins `action` via z.literal(...); the default (no/unknown
// action) variant is selected via a preprocess that maps any value outside the explicit
// action set to the "__default__" sentinel.

export const projectTemplateConfigHandlerRequest = z
  .object({
    action: z.enum(["add", "remove"]).meta({
      description:
        '"add" attaches templates to the project (requires CONFIGURATION_PROJECT_SELECT_FORM_TEMPLATES); "remove" detaches them (requires CONFIGURATION_PROJECT_REMOVE_FORM_TEMPLATES).',
    }),
    projectId: objectIdString.meta({
      description: "Project _id (ObjectId 24-hex string) to attach/detach templates for; from the projects listing.",
    }),
    templateIds: z.array(objectIdString).meta({
      description:
        "Forms TEMPLATE _ids (ObjectId 24-hex strings) to add to / remove from the project's usable templates; from the formController listing.",
    }),
  })
  .meta({
    description:
      "Attaches or detaches Form templates to/from a Project: action 'add' pushes a {id, noOfFormsOfThisTemplate:0} entry onto each Forms doc's projects[], action 'remove' pulls it. templateIds are Forms template _ids; projectId is the Project _id. Enforces project-level permission CONFIGURATION_PROJECT_SELECT_FORM_TEMPLATES for add and CONFIGURATION_PROJECT_REMOVE_FORM_TEMPLATES for remove. This configures which templates a Project can use; it does not create form instances.",
  });

export const projectTemplateConfigHandlerResponse = formStatusResponse.meta({
  description: "Response for project template config handler",
});

export const getFormWBSItemsAndAssociatedBoqItemsRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string), from fetchFormEntries; the form whose WBS+BOQ items are returned.",
    }),
    contractId: objectIdString.meta({
      description:
        "Parent Contract _id (ObjectId 24-hex string); RESOURCE_VIEW on it is required. The latest version is resolved automatically.",
    }),
    contractVersionId: objectIdString.optional().meta({
      description:
        "Optional contract version _id; the service resolves the contract's latest version, so this is not required.",
    }),
  })
  .meta({
    description:
      "For a single contract-based form instance, returns the WBS items attached to that form together with their associated BOQ items (used when the form is basedOnBudget). formId is the InspectionAndProgress instance _id (from fetchFormEntries) and contractId is the parent Contract _id; the contract's latest version is resolved automatically. Requires RESOURCE_VIEW on the Contract. Differs from getFormBoqItems (BOQ only) and getFormTasks (task-based forms).",
  });

export const getFormWBSItemsAndAssociatedBoqItemsResponse = formStatusResponse.meta({
  description: "Response for get form WBS items and associated BOQ items",
});

export const getFormsDataForChartRequest = z
  .object({
    contractId: objectIdString.meta({
      description: "Contract _id (ObjectId 24-hex string) to aggregate form counts for; RESOURCE_VIEW required.",
    }),
    contractVersionId: objectIdString.meta({
      description: "The specific Contract version _id (ObjectId 24-hex string) whose form instances are counted.",
    }),
  })
  .meta({
    description:
      "Returns aggregated counts of form instances for one Contract version, grouped by resourceType (form category) and status, for the contract overview chart — NOT the data of any single form. contractId is the Contract _id and contractVersionId is that contract's version _id. Requires RESOURCE_VIEW on the Contract.",
  });

export const getFormsDataForChartResponse = extendStatusResponse({
  result: z.unknown().optional(),
  count: z.number().nonnegative().optional(),
  data: z.unknown().optional(),
});

// Service only uses: formId (gets contractVersionId from form data)
export const getFormTasksRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) of a task-based contract form; from fetchFormEntries.",
    }),
    contractVersionId: objectIdString.optional().meta({
      description:
        "IGNORED by the service: the contract and version are read from the form document, so this input is not used.",
    }),
  })
  .meta({
    description:
      "For a task-based contract form instance, returns the schedule tasks attached to the form and their associated BOQ items, aggregated by originalTaskId. Only formId (the InspectionAndProgress instance _id) is needed — the contractId and version are read from the form document; contractVersionId in the payload is ignored. Requires RESOURCE_VIEW on the Contract. Use for basedOnTasks forms; use getFormWBSItems for budget/WBS forms.",
  });

export const getFormTasksResponse = formStatusResponse.meta({
  description: "Response for get form tasks",
});

export const getFormBoqItemsRequest = z
  .object({
    formId: objectIdString.meta({
      description:
        "InspectionAndProgress form-INSTANCE _id (ObjectId 24-hex string) of a contract form; from fetchFormEntries. The contract and version are derived from the form document. RESOURCE_VIEW on the resolved contract is required.",
    }),
  })
  .meta({
    description:
      "Returns the BOQ items attached to one contract form instance (in the response's boqItems array). Only formId (the InspectionAndProgress instance _id, from fetchFormEntries) is required — the contractId and version are derived from the form document. Requires RESOURCE_VIEW on the resolved Contract; returns an error if the form does not exist. Narrower than getFormWBSItems, which also returns WBS grouping.",
  });

export const getFormBoqItemsResponse = extendStatusResponse({
  result: z.unknown().optional(),
  count: z.number().nonnegative().optional(),
  boqItems: z.array(z.record(z.string(), z.unknown())).optional(),
});

export const getContractTaskHierarchyRequest = z
  .object({
    contractId: objectIdString.meta({
      description:
        "Contract _id (ObjectId 24-hex string) the task belongs to; RESOURCE_VIEW required (checked via getContractDetails).",
    }),
    originalTaskId: objectIdString.meta({
      description:
        "The task's originalTaskId (ObjectId 24-hex string) within the contract; the tree is built around this task and it is flagged isSelected.",
    }),
  })
  .meta({
    description:
      "Read-only: returns the task ancestry/descendant tree for one contract task, aggregating contractsHasTasks where allChilds or originalTaskId matches the given task; each returned node carries hasChild/parentId/WBSCode/title and an isSelected flag marking the requested task. Requires RESOURCE_VIEW permission on the contract (checked via getContractDetails). Inputs: contractId (a Contract's id) and originalTaskId (the task's originalTaskId within that Contract).",
  });

export const getContractTaskHierarchyResponse = formStatusResponse.meta({
  description: "Response for get contract task hierarchy",
});

export const getBase64StringForFormEntiresRequest = z
  .object({
    templateId: objectIdString.meta({
      description:
        "Forms TEMPLATE _id (ObjectId 24-hex string) — the selectedForm._id whose instances' image answers are returned; from the formController listing. Access limited to instances where the caller has RESOURCE_VIEW.",
    }),
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) scoping which form instances are read; from the projects listing.",
    }),
  })
  .meta({
    description:
      "Read-only: for a given Form template and Project, finds the InspectionAndProgress form instances the caller may view, reads each image-type answer's stored file from storage, and returns a nested map formId -> questionId -> base64 string (only sections without a sectionCode). Access is scoped to instances where the session user has RESOURCE_VIEW in the form's users[]. Inputs: templateId (the selectedForm/Forms template _id) and projectId (from the projects list). Note the endpoint name misspells 'Entries' as 'Entires'.",
  });

export const getBase64StringForFormEntiresResponse = formStatusResponse.meta({
  description: "Response for get base64 string for form entries",
});

export const formUserSettingsControllerRequest = z
  .object({
    action: z.string().meta({
      description:
        "Legacy dispatcher action selector (check/createForm/deleteFormUserSettings); the split endpoints omit it.",
    }),
    projectId: objectIdString.meta({
      description: "Project _id (ObjectId 24-hex string) the saved settings belong to; from the projects listing.",
    }),
    resourceType: z.string().meta({
      description: "Form category string (e.g. INSPECTION, PROGRESS_UPDATES) keying the FORM_SETTINGS entry.",
    }),
  })
  .meta({
    description: "Payload for form user settings controller",
  });

// Per-action payload schemas for formUserSettingsController. The controller dispatches on
// req.body.action across checkFormUserSettings / createForm / deleteFormUserSettings and a
// no-op default; every branch's service (checkFormUserSettings, createFormFromUserSettings,
// deleteFormUserSettings) reads the SAME body fields — projectId + resourceType — so all
// variants share identical non-action fields (mirrors the flat schema exactly).
export const formUserSettingsCheckSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) to check for a saved FORM_SETTINGS entry; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying the settings entry to check." }),
  })
  .meta({
    description:
      "Read-only: checks whether the calling user has a saved 'remember settings' entry (user_settings key FORM_SETTINGS) for the given Project and form resourceType; returns status plus isUserSettingExists (true/false). Does not create anything. Inputs: projectId (from the projects list) and resourceType (a form category string such as INSPECTION or PROGRESS_UPDATES). Use before createFormFromUserSettings to decide if quick-create is available.",
  });

export const formUserSettingsCreateSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) whose saved FORM_SETTINGS form is duplicated; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying which saved setting to quick-create from." }),
  })
  .meta({
    description:
      "Write (transactional): quick-creates a NEW form instance by duplicating the InspectionAndProgress form referenced in the caller's saved FORM_SETTINGS for this Project+resourceType — copies title (suffixed '(n)'), description, template, form flow (action state stripped), due date and attachments, then increments the source form's noOfDuplicatedOfThisForm. Fails with 'Settings does not exists' if no saved setting exists; space create permission is enforced inside the underlying save. Inputs: projectId and resourceType (same pair used in checkFormUserSettings). Unlike a raw form save, this reuses the user's remembered form as the template.",
  });

export const formUserSettingsDeleteSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string) whose saved FORM_SETTINGS entry is deleted; from the projects listing.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string (e.g. INSPECTION) keying which saved setting to delete." }),
  })
  .meta({
    description:
      "Write: deletes the calling user's saved 'remember settings' entry (user_settings FORM_SETTINGS) for the given Project and form resourceType, so quick-create is no longer offered for that combination. Inputs: projectId (from the projects list) and resourceType (form category string). Only affects the current user's own settings, not the underlying forms.",
  });

// The default (no-/unknown-action) branch is a controller no-op, but the flat schema still
// required projectId + resourceType, so the default variant keeps the same required fields to
// stay HTTP-equivalent.
const formUserSettingsDefaultSchema = z
  .object({
    projectId: objectIdString.meta({
      description:
        "Project _id (ObjectId 24-hex string); required for HTTP-equivalence with the split endpoints. The default branch is a no-op.",
    }),
    resourceType: z
      .string()
      .meta({ description: "Form category string; required for HTTP-equivalence. The default branch is a no-op." }),
  })
  .meta({ description: "Payload for form user settings controller (default)" });

// Discriminated union composed from the per-action schemas above — single source of truth that
// validates HTTP and lets the MCP bridge auto-expand one tool per action. A preprocess maps any
// value outside the explicit action set to the "__default__" sentinel (the no-op branch).

export const formUserSettingsControllerResponse = formStatusResponse.meta({
  description: "Response for form user settings controller",
});

// ============================================================================
// Type Exports - Single Source of Truth for Frontend Consumption
// ============================================================================

// Add Section to Library Types
export type AddSectionToLibraryRequest = z.infer<typeof addSectionToLibraryRequest>;
export type AddSectionToLibraryResponse = z.infer<typeof addSectionToLibraryResponse>;

// Delete Sections from Library Types
export type DeleteSectionsFromLibraryRequest = z.infer<typeof deleteSectionsFromLibraryRequest>;
export type DeleteSectionsFromLibraryResponse = z.infer<typeof deleteSectionsFromLibraryResponse>;

// Fetch Form Entries Types
export type FetchFormEntriesRequest = z.infer<typeof fetchFormEntriesRequest>;
export type FetchFormEntriesResponse = z.infer<typeof fetchFormEntriesResponse>;

// Form User Settings Controller Types
export type FormUserSettingsControllerRequest = z.infer<typeof formUserSettingsControllerRequest>;
export type FormUserSettingsControllerResponse = z.infer<typeof formUserSettingsControllerResponse>;

// Get Base64 String for Form Entries Types
export type GetBase64StringForFormEntiresRequest = z.infer<typeof getBase64StringForFormEntiresRequest>;
export type GetBase64StringForFormEntiresResponse = z.infer<typeof getBase64StringForFormEntiresResponse>;

// Get Contract Form BOQ Items Types
export type GetContractFormBoqItemsRequest = z.infer<typeof getContractFormBoqItemsRequest>;
export type GetContractFormBoqItemsResponse = z.infer<typeof getContractFormBoqItemsResponse>;

// Get Contract Task Hierarchy Types
export type GetContractTaskHierarchyRequest = z.infer<typeof getContractTaskHierarchyRequest>;
export type GetContractTaskHierarchyResponse = z.infer<typeof getContractTaskHierarchyResponse>;

// Get Entities from Library Types
export type GetEntitesFromLibraryRequest = z.infer<typeof getEntitesFromLibraryRequest>;
export type GetEntitesFromLibraryResponse = z.infer<typeof getEntitesFromLibraryResponse>;

// Get Form BOQ Items Types
export type GetFormBoqItemsRequest = z.infer<typeof getFormBoqItemsRequest>;
export type GetFormBoqItemsResponse = z.infer<typeof getFormBoqItemsResponse>;

// Get Form Tasks Types
export type GetFormTasksRequest = z.infer<typeof getFormTasksRequest>;
export type GetFormTasksResponse = z.infer<typeof getFormTasksResponse>;

// Get Form WBS Items and Associated BOQ Items Types
export type GetFormWBSItemsAndAssociatedBoqItemsRequest = z.infer<typeof getFormWBSItemsAndAssociatedBoqItemsRequest>;
export type GetFormWBSItemsAndAssociatedBoqItemsResponse = z.infer<typeof getFormWBSItemsAndAssociatedBoqItemsResponse>;

// Get Forms Data for Chart Types
export type GetFormsDataForChartRequest = z.infer<typeof getFormsDataForChartRequest>;
export type GetFormsDataForChartResponse = z.infer<typeof getFormsDataForChartResponse>;

// Project Template Config Handler Types
export type ProjectTemplateConfigHandlerRequest = z.infer<typeof projectTemplateConfigHandlerRequest>;
export type ProjectTemplateConfigHandlerResponse = z.infer<typeof projectTemplateConfigHandlerResponse>;

// Form Types Controller Types
export type GetTemplateTypesResponse = z.infer<typeof getTemplateTypesResponse>;

// Form Types Controller Request — individual action variants
export type InsertTemplateTypeRequest = z.infer<typeof insertTemplateTypeSchema>;
export type UpdateTemplateTypeRequest = z.infer<typeof updateTemplateTypeSchema>;
export type DeleteTemplateTypeRequest = z.infer<typeof deleteTemplateTypeSchema>;
export type TemplateTypesDefaultRequest = z.infer<typeof templateTypesDefaultSchema>;

// Form Types Controller Request — discriminated union of all action variants
export type GetTemplateTypesRequest =
  | InsertTemplateTypeRequest
  | UpdateTemplateTypeRequest
  | DeleteTemplateTypeRequest
  | TemplateTypesDefaultRequest;

// Form Controller Types
export type GetTemplatesResponse = z.infer<typeof getTemplatesResponse>;

// Form Controller Request — individual action variants
export type SaveTemplateRequest = z.infer<typeof saveTemplateSchema>;
export type EditTemplateRequest = z.infer<typeof editTemplateSchema>;
export type DeleteTemplateRequest = z.infer<typeof deleteTemplateSchema>;
export type ArchiveTemplateRequest = z.infer<typeof archiveTemplateSchema>;
export type UpdateUsedTemplateRequest = z.infer<typeof updateUsedTemplateSchema>;
export type TemplateDefaultRequest = z.infer<typeof templateDefaultSchema>;

// Form Controller Request — discriminated union of all action variants
export type GetTemplatesRequest =
  | SaveTemplateRequest
  | EditTemplateRequest
  | DeleteTemplateRequest
  | ArchiveTemplateRequest
  | UpdateUsedTemplateRequest
  | TemplateDefaultRequest;

// Task Inspection and Progress Forms Controller Types
export type TaskInspectionAndProgressFormsControllerResponse = z.infer<
  typeof taskInspectionAndProgressFormsControllerResponse
>;

// Task Inspection and Progress Forms Controller Request — individual action variants
export type TipfUpdateRequest = z.infer<typeof tipfUpdateSchema>;
export type TipfSaveRequest = z.infer<typeof tipfSaveSchema>;
export type TipfDeleteRequest = z.infer<typeof tipfDeleteSchema>;
export type TipfArchiveRequest = z.infer<typeof tipfArchiveSchema>;
export type TipfUpdatePermissionRequest = z.infer<typeof tipfUpdatePermissionSchema>;
export type TipfDefaultRequest = z.infer<typeof tipfDefaultSchema>;

// Task Inspection and Progress Forms Controller Request — discriminated union of all action variants
export type TaskInspectionAndProgressFormsControllerRequest =
  | TipfUpdateRequest
  | TipfSaveRequest
  | TipfDeleteRequest
  | TipfArchiveRequest
  | TipfUpdatePermissionRequest
  | TipfDefaultRequest;

// Form Flow Controller Types
export type FormFlowControllerResponse = z.infer<typeof formFlowControllerResponse>;

// Form Flow Controller Request — individual action variants
export type FormFlowUpdateRequest = z.infer<typeof formFlowUpdateSchema>;
export type FormFlowSaveRequest = z.infer<typeof formFlowSaveSchema>;
export type FormFlowDuplicateRequest = z.infer<typeof formFlowDuplicateSchema>;
export type FormFlowDeleteRequest = z.infer<typeof formFlowDeleteSchema>;
export type FormFlowAddToLibraryRequest = z.infer<typeof formFlowAddToLibrarySchema>;
export type FormFlowAddFromLibraryRequest = z.infer<typeof formFlowAddFromLibrarySchema>;
export type FormFlowArchiveRequest = z.infer<typeof formFlowArchiveSchema>;
export type FormFlowDefaultRequest = z.infer<typeof formFlowDefaultSchema>;

// Form Flow Controller Request — discriminated union of all action variants
export type FormFlowControllerRequest =
  | FormFlowUpdateRequest
  | FormFlowSaveRequest
  | FormFlowDuplicateRequest
  | FormFlowDeleteRequest
  | FormFlowAddToLibraryRequest
  | FormFlowAddFromLibraryRequest
  | FormFlowArchiveRequest
  | FormFlowDefaultRequest;
